/**
 * Fitting an ordinary photograph into browser storage.
 *
 * The upload used to refuse anything over 400 KB, which in practice means it
 * refused almost every photograph anyone actually has — a phone camera produces
 * three or four megabytes without trying. Telling an operator their picture is
 * too big, when the machine they are standing at could resize it in fifty
 * milliseconds, is the application making its own problem into the user's.
 *
 * So it resizes instead. The card renders at 288 × 192 CSS pixels, so 864 × 576
 * is three times that — enough for any display density in use — and anything
 * larger is detail no one will ever see. JPEG at 0.85 keeps it comfortably
 * inside the quota.
 *
 * It is still capable of failing: a file that is not an image, or one the
 * browser cannot decode, or a picture so large the canvas refuses it. Those
 * come back as a rejected promise with a sentence that says which, because
 * "could not use that file" tells an operator nothing about what to do next.
 */

/** Three times the card's own size, which is past what any display resolves. */
const MAX_W = 864;
const MAX_H = 576;
const QUALITY = 0.85;

/** Roughly what a data URI of this length costs in storage. */
export const approxBytes = (dataUrl: string): number => Math.ceil((dataUrl.length * 3) / 4);

export function downscaleToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("That is not an image file."));
      return;
    }

    const url = URL.createObjectURL(file);
    const img = new Image();

    img.onerror = () => {
      URL.revokeObjectURL(url);
      /* The overwhelmingly common cause, and the one with an obvious fix. */
      reject(
        new Error(
          file.name.toLowerCase().endsWith(".heic")
            ? "This browser cannot read HEIC files — the format an iPhone saves by default. Export it as JPEG and try again."
            : "The browser could not decode that image.",
        ),
      );
    };

    img.onload = () => {
      URL.revokeObjectURL(url);
      const scale = Math.min(1, MAX_W / img.width, MAX_H / img.height);
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));

      const canvas = document.createElement("canvas");
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("This browser would not give the page a canvas to resize with."));
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);

      try {
        /* JPEG, not PNG: a photograph as PNG is several times larger for no
           visible gain. A picture with transparency loses it here, which is
           the right trade when the alternative is not fitting at all. */
        resolve(canvas.toDataURL("image/jpeg", QUALITY));
      } catch {
        reject(new Error("The image could not be read back after resizing."));
      }
    };

    img.src = url;
  });
}
